// Framework export/import — portable, code-tabanlı format (id değil).
// Tek versiyon ağacı + framework metadata (L2). Spec: docs/superpowers/specs/2026-06-26-framework-export-import-design.md
//
// ⚠️ KİT SENKRONU: Bu şema docs/framework-kit/framework.schema.json'ın kaynağıdır
// (Framework Yazım Kiti — spec 2026-09-08). Alan ekle/çıkar/açıklama değiştir →
// `pnpm kit:schema` çalıştır, docs/framework-kit/yazim-kilavuzu.md ilgili bölümünü güncelle,
// kapak rozetinde sürüm bump'la (CLAUDE.md "Framework Yazım Kiti"). Drift testi
// (apps/backend/src/scripts/framework-kit-schema.test.ts) unutmayı yakalar.
// `.describe()` metinleri müşteriye giden JSON Schema'da `description` olur — Türkçe, kısa, kural odaklı.
import { z } from "zod";
import { compositeHasFilePart, QuestionType, QuestionOptionsSchema } from "./questions.js";
import { ApplicablePeriodsSchema, PERIOD_TYPES } from "./period.js";

export const FRAMEWORK_EXPORT_FORMAT_VERSION = 1 as const;

export const ExportQuestionSchema = z
  .object({
    code: z
      .string()
      .min(1)
      .describe(
        "Soru kodu. Bölüm içinde tekil, ASCII, boşluksuz, stabil; standardın numaralandırmasını izler (örn. GRI-305-1). Yıl/dönem gömülmez. Yanıt import'unda eşleşme anahtarı.",
      ),
    label: z.string().min(1).describe("Sorunun formda görünen başlığı."),
    description: z
      .string()
      .nullable()
      .default(null)
      .describe("Soruyu açan kısa açıklama; yoksa null."),
    helperText: z
      .string()
      .nullable()
      .default(null)
      .describe("Giriş alanının altında görünen ipucu; yoksa null."),
    type: QuestionType.describe("Soru tipi. options.type ile AYNI olmalı."),
    options: QuestionOptionsSchema.describe(
      "Tipe özgü ayarlar (discriminated union). Bkz. definitions/QuestionOptions.",
    ),
    required: z
      .boolean()
      .describe("Zorunlu mu. composite'te parçalardan türetilir; yazılan değer yok sayılır."),
    enabled: z.boolean().describe("Formda gösterilsin mi. Yeni framework'te true."),
    // Opt-out: true = bu soruda önceki yıl önerisi gizlenir. Eski exportlardaki
    // priorYearHint (opt-in) anahtarı .strip() ile atılır → default false doğru göçtür.
    priorYearHintDisabled: z
      .boolean()
      .default(false)
      .describe("true = bu soruda önceki yıl önerisi gizlenir. Varsayılan false."),
    allowEvidence: z
      .boolean()
      .describe("Kanıt belgesi yüklenebilsin mi. Dosya parçası içeren composite'te false olmalı."),
    allowNote: z.boolean().describe("Kullanıcı not ekleyebilsin mi."),
    aiContext: z
      .string()
      .nullable()
      .default(null)
      .describe(
        "L4 — soruya özel AI stil ipucu, 1-3 cümle, SEÇİCİ: framework genelinde soruların en az %5'i, en çok %20'si; bölüm başına en az 1. Yıla ait olgu içermez.",
      ),
    order: z.number().int().describe("Bölüm içi sıra, 0'dan başlar, ardışık."),
    // Raporlama dönemi (K7 D3): default YOK — import bölümden miras alır (servis create ile aynı türetim).
    applicablePeriods: ApplicablePeriodsSchema.optional(),
  })
  .strip()
  .refine((q) => q.type === q.options.type, {
    message: "Soru tipi ve options tipi eşleşmeli",
    path: ["options"],
  })
  // M15 D15 — import yolu da aynı kuralı uygular (UI gizlemesi dosyayı korumaz)
  .refine((q) => !(q.allowEvidence && compositeHasFilePart(q.options)), {
    message: "Dosya parçası içeren bileşik soruda kanıt belgesi açılamaz",
    path: ["allowEvidence"],
  })
  .describe("Bir soru. Ek alan `sourceRef` (rapordaki kaynak) kabul edilir, import yok sayar.");
export type ExportQuestion = z.infer<typeof ExportQuestionSchema>;

export const ExportSectionSchema = z
  .object({
    code: z
      .string()
      .min(1)
      .describe("Bölüm kodu. Framework içinde tekil, ASCII; standardın kodu (örn. GRI-305)."),
    label: z.string().min(1).describe("Bölüm başlığı."),
    description: z.string().nullable().default(null).describe("Bölüm açıklaması; yoksa null."),
    order: z.number().int().describe("Framework içi sıra, 0'dan başlar, ardışık."),
    sectionPrompt: z
      .string()
      .default("")
      .describe(
        "L3 — bölüm hedefi, uzunluk aralığı, alt başlık düzeni; 40-200 kelime, soru sayısıyla orantılı (1-3 soru 40-60, 20+ soru 150-200). Stil/yapı anlatır, yıla ait olgu (rakam, tarih) içermez.",
      ),
    // Kit v1.1 — harita sözlüğü: import (Geçmiş Rapor Aktarımı) Faz 1 bu eş anlamlıları kullanır.
    // formatVersion 1 kalır: eski dosyalar alan olmadan geçer (default []), eski deployment strip eder.
    mappingHints: z
      .array(z.string().min(1).max(120))
      .max(30)
      .default([])
      .describe(
        'Bu bölümün raporlarda geçebilecek başlık varyantları (eş anlamlılar), ≤30 madde. Rapordan yanıt aktarımında bölüm ↔ sayfa eşlemesi için kullanılır; örn. ["Enerji Yönetimi", "Enerji Verimliliği ve Yenilenebilir Enerji"].',
      ),
    // Raporlama dönemi (K7 D3): bölümde TAVAN — yazılmazsa hepsi (annual/q1/q2/q3).
    applicablePeriods: ApplicablePeriodsSchema.default([...PERIOD_TYPES]),
    questions: z.array(ExportQuestionSchema).describe("Bölümün soruları, order sırasıyla."),
  })
  .strip()
  .describe("Bir bölüm ve soruları.");
export type ExportSection = z.infer<typeof ExportSectionSchema>;

export const FrameworkExportV1Schema = z
  .object({
    formatVersion: z.literal(FRAMEWORK_EXPORT_FORMAT_VERSION).describe("Sabit 1."),
    exportedAt: z
      .string()
      .describe("Üretim zamanı. ISO 8601 önerilir (2026-09-10T12:00:00Z), zorunlu değil."),
    source: z
      .object({
        app: z
          .string()
          .describe(
            'Üreten araç. Mahir dışa aktarması "mahir-ai" yazar; kit çıktısı "codex" yazmalıdır — Mahir kit konvansiyon uyarılarını (sourceRef, yakalayıcı, mappingHints) yalnız "mahir-ai" dışındaki dosyalarda uygular.',
          ),
        frameworkName: z.string().describe("Bilgi amaçlı; framework.name ile aynı yazılır."),
        versionNumber: z.string().describe("Bilgi amaçlı; version.versionNumber ile aynı."),
      })
      .strip()
      .describe("Kaynak bilgisi; import kullanmaz."),
    framework: z
      .object({
        name: z
          .string()
          .min(1)
          .describe(
            'Framework adı: standart + sürüm (örn. "GRI 2021", "TSRS 2"). Şirket adı framework adına GİRMEZ.',
          ),
        description: z.string().nullable().default(null).describe("Kısa açıklama; yoksa null."),
        frameworkPrompt: z
          .string()
          .default("")
          .describe(
            "L2 — standarda özgü terminoloji, bölüm hiyerarşisi, ton ve tekrar eden yapısal kalıplar; 300-800 kelime. Yıla ait olgu içermez.",
          ),
        supportsQuarterly: z
          .boolean()
          .default(false)
          .describe(
            "true ise bu framework'ten çeyreklik (q1/q2/q3) proje açılabilir. Sette ara dönem raporu yoksa yazma (false). Dönem filtresi her zaman uygulanır; bu alan yalnız izin ve düzenleme arayüzünü açar.",
          ),
        sectionCodesAreStandardRefs: z
          .boolean()
          .default(false)
          .describe(
            "true yalnız bölüm kodları bir standardın kendi referansıysa (GRI 2-1, TSRS vb.); raporda bölüm sonunda 'İlgili standart' dipnotu basılır. SPK/BDDK faaliyet raporu ve özel (iç kodlu) çerçevelerde false — varsayılan.",
          ),
      })
      .strip()
      .describe("Framework metadata (L2 dahil)."),
    version: z
      .object({
        versionNumber: z
          .string()
          .min(1)
          .describe("Serbest metin; konvansiyon vYYYY.N (örn. v2026.1)."),
        sections: z.array(ExportSectionSchema).describe("Bölümler, order sırasıyla."),
      })
      .strip()
      .describe("Tek versiyonun tam ağacı."),
  })
  .strip()
  .describe(
    "Mahir AI framework taşıma dosyası (formatVersion 1): bir framework + tek versiyonun tam bölüm/soru ağacı. Import her zaman yeni bir taslak framework açar.",
  );
export type FrameworkExportV1 = z.infer<typeof FrameworkExportV1Schema>;
